import { Group, Menu, Text, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconPencil, IconTrashX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { EDIT_EVIDENCE_FIELD_PAGE_PATH } from 'routes/paths';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Parameters of the OrganizationActions component.
 */
interface EvidenceFieldActionsProps {
  evidenceFieldId: number;
  fieldName: string;
  appliesToPeople?: boolean;
  compactOnTablet?: boolean;
  onDelete?: () => void;
}

/**
 * The actions which can be performed on a single row.
 */
export default function EvidenceFieldActions({
  evidenceFieldId,
  fieldName,
  appliesToPeople = false,
  compactOnTablet = false,
  onDelete = noop,
}: EvidenceFieldActionsProps) {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';

  const handleDelete = useCallback(
    () =>
      confirm({
        title: 'Odstrániť evidenčné pole',
        content: `Ste si istý, že chcete odstrániť evidenčné pole "${fieldName}"? Všetky údaje na ${
          appliesToPeople ? 'osobách' : 'zariadeniach'
        } budú nenávratne zmazané.`,
        onConfirm: () => {
          const deleteEvidenceField = getAction('DeviceDeleteEvidenceField');

          deleteEvidenceField({ parameters: { evidenceFieldId: String(evidenceFieldId) } })
            .then(onDelete)
            .catch(panic);
        },
      }),
    [appliesToPeople, evidenceFieldId, fieldName, getAction, onDelete]
  );

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (isCompact ? (
          <Tooltip label="Editovať" events={{ hover: true, focus: true, touch: true }}>
            <ActionIcon
              variant="secondary"
              size="md"
              component={Link}
              to={EDIT_EVIDENCE_FIELD_PAGE_PATH.insert({ evidenceFieldId })}
            >
              <IconPencil />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Button
            component={Link}
            to={EDIT_EVIDENCE_FIELD_PAGE_PATH.insert({ evidenceFieldId })}
            size="md"
            variant="secondary"
            leftIcon={<IconPencil />}
          >
            Editovať
          </Button>
        ))}
      <Menu withinPortal position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'} aria-label={`Ďalšie akcie: ${fieldName}`}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>
            <Text maw={160} truncate>
              {fieldName}
            </Text>
          </Menu.Label>
          {menuOnly && (
            <Menu.Item
              component={Link}
              to={EDIT_EVIDENCE_FIELD_PAGE_PATH.insert({ evidenceFieldId })}
              icon={<IconPencil stroke={1.5} size={20} />}
            >
              Editovať
            </Menu.Item>
          )}
          <Menu.Item color="red.8" onClick={handleDelete} icon={<IconTrashX stroke={1.5} size={20} />}>
            Odstrániť
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
